/**
 * Command Processor
 * Processa comandos de IA e coordena execução
 */

import { aiClient } from './aiClient';
import { streamingService } from './streamingService';
import { handleAIError, logAIError } from './errorHandler';
import type { AICommand, AIRequest, AIResponse } from './types';

export interface CommandExecutionOptions {
  selectedText?: string;
  context?: string;
  streaming?: boolean;
  options?: Record<string, any>;
  onChunk?: (content: string) => void;
  onComplete?: (content: string) => void;
  onError?: (error: string) => void;
}

/**
 * Processador de comandos de IA
 */
export class CommandProcessor {
  private static instance: CommandProcessor | null = null;

  static getInstance(): CommandProcessor {
    if (!CommandProcessor.instance) {
      CommandProcessor.instance = new CommandProcessor();
    }
    return CommandProcessor.instance;
  }

  /**
   * Executar comando de IA
   */
  async executeCommand(
    command: AICommand,
    options: CommandExecutionOptions = {}
  ): Promise<AIResponse> {
    const {
      selectedText = '',
      context = '',
      streaming = false,
      onChunk,
      onComplete,
      onError,
    } = options;

    console.log('🤖 Executando comando:', command.label, {
      hasSelection: !!selectedText,
      streaming,
    });

    // Validar se comando requer seleção
    if (command.requiresSelection && !selectedText) {
      const error = `Comando "${command.label}" requer texto selecionado`;
      onError?.(error);
      return {
        success: false,
        content: '',
        error,
      };
    }

    // Preparar requisição
    const request: AIRequest = {
      command: command.type,
      content: context,
      selectedText,
      streaming: streaming,
      options: {
        type: command.type,
        label: command.label,
        documentId: window.location.pathname.split('/').pop(),
        // Adicionar opções específicas do comando se existirem
        ...options.options,
      },
    };

    try {
      if (streaming) {
        return await this.executeStreamingCommand(request, {
          onChunk,
          onComplete,
          onError,
        });
      } else {
        return await this.executeRegularCommand(command, request);
      }
    } catch (error) {
      const aiError = handleAIError(error);
      logAIError(`executeCommand(${command.label})`, aiError, request);
      
      onError?.(aiError.message);
      return {
        success: false,
        content: '',
        error: aiError.message,
      };
    }
  }

  /**
   * Executar comando regular (não streaming)
   */
  private async executeRegularCommand(
    command: AICommand,
    request: AIRequest
  ): Promise<AIResponse> {
    switch (command.type) {
      case 'suggest':
        return await aiClient.suggest(request);
      
      
      case 'refactor':
      case 'summarize':
      case 'tone':
        // Comandos de refatoração usam ai-refactor
        return await aiClient.refactor(request);
      
      default:
        return {
          success: false,
          content: '',
          error: `Comando "${command.type}" não implementado`,
        };
    }
  }

  /**
   * Executar comando com streaming
   */
  private async executeStreamingCommand(
    request: AIRequest,
    options: {
      onChunk?: (content: string) => void;
      onComplete?: (content: string) => void;
      onError?: (error: string) => void;
    }
  ): Promise<AIResponse> {
    return new Promise((resolve) => {
      const stream = aiClient.streamRequest(request);
      let finalContent = '';

      streamingService.startStream(stream, {
        debounceMs: 200,
        onChunk: (content) => {
          options.onChunk?.(content);
        },
        onComplete: (content) => {
          finalContent = content;
          options.onComplete?.(content);
          resolve({
            success: true,
            content,
            streaming: true,
          });
        },
        onError: (error) => {
          options.onError?.(error);
          resolve({
            success: false,
            content: finalContent,
            error,
          });
        },
      });
    });
  }

  /**
   * Cancelar execução ativa
   */
  cancelExecution(): void {
    console.log('🚫 Cancelando execução de comando');
    aiClient.cancelActiveRequest();
    streamingService.stopStream();
  }

  /**
   * Verificar se está executando
   */
  get isExecuting(): boolean {
    return streamingService.isStreaming;
  }
}

export const commandProcessor = CommandProcessor.getInstance();